import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Box } from '@mui/material';
import { keyframes } from '@mui/system';
import { setBootPosterPreloadUrl } from '../../services/bootCoordinator';

// ============================================
// ANIMATIONS
// ============================================

const slideInFromRight = keyframes`
  from { transform: translateX(110%); }
  to   { transform: translateX(0); }
`;

const slideOutToLeft = keyframes`
  from { transform: translateX(0); }
  to   { transform: translateX(-110%); }
`;

// Ken Burns variants — each poster drifts differently
const kenBurns1 = keyframes`
  from { transform: scale(1.08) translate(2%, 1%); }
  to   { transform: scale(1.0) translate(-2%, -1%); }
`;

const kenBurns2 = keyframes`
  from { transform: scale(1.0) translate(-1%, -2%); }
  to   { transform: scale(1.08) translate(1%, 2%); }
`;

const kenBurns3 = keyframes`
  from { transform: scale(1.06) translate(1%, -2%); }
  to   { transform: scale(1.0) translate(-1%, 2%); }
`;

// ============================================
// CONSTANTS
// ============================================

// All URLs verified 200 via curl against image.tmdb.org
const POSTERS = [
  'https://image.tmdb.org/t/p/w500/9JhfVOveaY00o8njQu2Xrp4YWud.jpg', // Cinema Paradiso
  'https://image.tmdb.org/t/p/w500//frZj5djlU9hFEjMcL21RJZVuG5O.jpg', // Pather Panchali  
  'https://image.tmdb.org/t/p/w500/3b4fRUYXFaGVDqkb0ikjB4cn7ub.jpg', // Arrival
  'https://image.tmdb.org/t/p/w500/8hoD5BQuUV9dDecAbiyVIxLjzZ9.jpg', // Dune
  'https://image.tmdb.org/t/p/w500/r3UKPGdQC5mPiA9bE9otM9CN4lW.jpg', // The Dark Knight
  'https://image.tmdb.org/t/p/w500/iYypPT4bhqXfq1b6EnmxvRt6b2Y.jpg', // In the Mood for Love
  'https://image.tmdb.org/t/p/w500/rtGDOeG9LzoerkDGZF9dnVeLppL.jpg', // My Neighbor Totoro
  'https://image.tmdb.org/t/p/w500/b2hGF0jHZjP9OXWr7LF8P1iq3XP.jpg', // Ratatouille
  'https://image.tmdb.org/t/p/w500/5rhTDKUhPYvpdQIijFIs5VoWsON.jpg', // The Odyssey
  'https://image.tmdb.org/t/p/w500/bX2xnavhMYjWDoZp1VM6VnU1xwe.jpg', // The Good, the Bad and the Ugly
  'https://image.tmdb.org/t/p/w500/vQWk5YBFWF4bZaofAbv0tShwBvQ.jpg', // Pulp Fiction
  'https://image.tmdb.org/t/p/w500/15uOEfqBNTVtDUT7hGBVCka0rZz.jpg', // Vertigo
  'https://image.tmdb.org/t/p/w500/nSxDa3M9aMvGVLoItzWTepQ5h5d.jpg', // Amélie
];

const TRANSITION_DURATION = 400; // ms — slide animation
const MIN_DISPLAY_TIME = 2000;   // ms — minimum time before trying to advance
const MAX_WAIT_TIME = 5000;      // ms — max wait for image before advancing anyway

const KEN_BURNS = [kenBurns1, kenBurns2, kenBurns3];

// ============================================
// MODULE-LEVEL SHUFFLE
// Runs once when this module is first imported (before any component renders).
// Registers the first poster URL with bootCoordinator so it can preload it
// while the native splash is still showing.
// ============================================

const SHUFFLED_POSTERS = [...POSTERS].sort(() => Math.random() - 0.5);
setBootPosterPreloadUrl(SHUFFLED_POSTERS[0]);

// ============================================
// COMPONENT
// ============================================

const BootScreen: React.FC = () => {
  const [shuffled] = useState(() => SHUFFLED_POSTERS);

  const [currentIndex, setCurrentIndex] = useState(0);
  const [nextIndex, setNextIndex] = useState(1);
  const [isTransitioning, setIsTransitioning] = useState(false);

  // Track which images are loaded
  const loadedRef = useRef<Set<number>>(new Set());
  const [, forceUpdate] = useState(0);

  const advanceRef = useRef<(() => void) | null>(null);
  const minTimeElapsedRef = useRef(false);
  const nextReadyRef = useRef(false);

  const tryAdvance = useCallback(() => {
    if (minTimeElapsedRef.current && nextReadyRef.current && !isTransitioning) {
      minTimeElapsedRef.current = false;
      nextReadyRef.current = false;

      setIsTransitioning(true);
      setTimeout(() => {
        setCurrentIndex(c => (c + 1) % shuffled.length);
        setNextIndex(n => (n + 1) % shuffled.length);
        setIsTransitioning(false);
      }, TRANSITION_DURATION);
    }
  }, [isTransitioning, shuffled.length]);

  advanceRef.current = tryAdvance;

  useEffect(() => {
    // Start the cycle
    const startCycle = () => {
      minTimeElapsedRef.current = false;
      nextReadyRef.current = false;

      // Minimum display timer
      const minTimer = setTimeout(() => {
        minTimeElapsedRef.current = true;
        advanceRef.current?.();
      }, MIN_DISPLAY_TIME);

      // Max wait fallback — advance even if image didn't load
      const maxTimer = setTimeout(() => {
        nextReadyRef.current = true;
        advanceRef.current?.();
      }, MAX_WAIT_TIME);

      return () => {
        clearTimeout(minTimer);
        clearTimeout(maxTimer);
      };
    };

    let cleanup = startCycle();

    return () => cleanup();
  // Re-run cycle each time currentIndex changes (after transition completes)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentIndex]);

  const handleNextImageLoad = useCallback((index: number) => {
    loadedRef.current.add(index);
    if (index === nextIndex) {
      nextReadyRef.current = true;
      forceUpdate(n => n + 1);
      advanceRef.current?.();
    }
  }, [nextIndex]);

  const cardBase = {
    position: 'absolute' as const,
    inset: 0,
    borderRadius: 'inherit',
    overflow: 'hidden' as const,
  };

  return (
    <Box
      data-testid="boot-screen"
      sx={{
        position: 'fixed',
        inset: 0,
        background: '#0a0a0a',
        zIndex: 9999,
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 4,
      }}
    >
      {/* Card stack */}
      <Box sx={{ position: 'relative', width: 200, height: 300 }}>

        {/* Ghost card 2 — furthest back */}
        <Box sx={{
          position: 'absolute',
          width: 200, height: 300,
          borderRadius: '12px',
          background: '#1a1a1a',
          top: -10, left: 12,
          transform: 'scale(0.93)',
          transformOrigin: 'bottom center',
          zIndex: 1,
        }} />

        {/* Ghost card 1 — middle */}
        <Box sx={{
          position: 'absolute',
          width: 200, height: 300,
          borderRadius: '12px',
          background: '#222222',
          top: -5, left: 6,
          transform: 'scale(0.96)',
          transformOrigin: 'bottom center',
          zIndex: 2,
        }} />

        {/* Active card area */}
        <Box sx={{
          position: 'absolute',
          width: 200, height: 300,
          borderRadius: '12px',
          overflow: 'hidden',
          boxShadow: '0 24px 60px rgba(0,0,0,0.85)',
          zIndex: 3,
        }}>
          {/* Current poster — slides out left */}
          <Box
            key={`cur-${currentIndex}`}
            sx={{
              ...cardBase,
              background: '#111',
              animation: isTransitioning
                ? `${slideOutToLeft} ${TRANSITION_DURATION}ms ease-in-out both`
                : undefined,
            }}
          >
            <img
              src={shuffled[currentIndex]}
              alt=""
              style={{
                width: '100%', height: '100%', objectFit: 'cover', display: 'block',
                animation: `${KEN_BURNS[currentIndex % 3]} ${MIN_DISPLAY_TIME + MAX_WAIT_TIME}ms ease-in-out both`,
              }}
            />
          </Box>

          {/* Next poster — slides in from right */}
          {isTransitioning && (
            <Box
              key={`nxt-${nextIndex}`}
              sx={{
                ...cardBase,
                background: '#111',
                animation: `${slideInFromRight} ${TRANSITION_DURATION}ms ease-in-out both`,
              }}
            >
              <img
                src={shuffled[nextIndex]}
                alt=""
                style={{
                  width: '100%', height: '100%', objectFit: 'cover', display: 'block',
                  animation: `${KEN_BURNS[nextIndex % 3]} ${MIN_DISPLAY_TIME + MAX_WAIT_TIME}ms ease-in-out both`,
                }}
              />
            </Box>
          )}
        </Box>
      </Box>

      {/* Full dark logo */}
      <img
        src="/assets/Theme=Dark, State=Main.svg"
        alt="Watchlists"
        style={{ height: 64, width: 'auto', objectFit: 'contain' }}
      />

      {/* Preload next image — hidden off-screen */}
      <img
        key={`pre-${nextIndex}`}
        src={shuffled[nextIndex]}
        alt=""
        onLoad={() => handleNextImageLoad(nextIndex)}
        style={{ position: 'fixed', top: -9999, left: -9999, width: 1, height: 1, opacity: 0 }}
      />
    </Box>
  );
};

export default BootScreen;
